/**
 * Login Preferences Utility
 * 
 * Manages user login method preferences using cookies.
 * Stores whether the user prefers email code or password login.
 */

export type LoginMethod = 'email_code' | 'password';

const LOGIN_PREFERENCE_COOKIE_NAME = 'login_preference';
const LOGIN_PREFERENCE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

/**
 * Get the user's preferred login method from cookie
 * @returns The preferred login method or null if not set
 */
export function getLoginPreference(): LoginMethod | null {
  try {
    const cookies = document.cookie.split(';');
    const preferenceCookie = cookies.find(cookie => 
      cookie.trim().startsWith(`${LOGIN_PREFERENCE_COOKIE_NAME}=`)
    );
    
    if (preferenceCookie) {
      const value = preferenceCookie.split('=')[1]?.trim();
      if (value === 'email_code' || value === 'password') {
        return value as LoginMethod;
      }
    }
    
    return null;
  } catch (error) {
    console.warn('[loginPreferences] Error reading login preference cookie:', error);
    return null;
  }
}

/**
 * Set the user's preferred login method in cookie
 * @param method The login method to save ('email_code' or 'password')
 */
export function setLoginPreference(method: LoginMethod): void {
  try {
    document.cookie = `${LOGIN_PREFERENCE_COOKIE_NAME}=${method}; path=/; max-age=${LOGIN_PREFERENCE_COOKIE_MAX_AGE}; SameSite=Lax`;
    console.log(`[loginPreferences] Saved login preference: ${method}`);
  } catch (error) {
    console.warn('[loginPreferences] Error saving login preference cookie:', error);
  }
}

/**
 * Clear the login preference cookie
 */
export function clearLoginPreference(): void {
  try {
    document.cookie = `${LOGIN_PREFERENCE_COOKIE_NAME}=; path=/; max-age=0`;
    console.log('[loginPreferences] Cleared login preference');
  } catch (error) {
    console.warn('[loginPreferences] Error clearing login preference cookie:', error);
  }
}

/**
 * Check if the user has a saved login preference
 * @returns true if a preference is saved, false otherwise
 */
export function hasLoginPreference(): boolean {
  return getLoginPreference() !== null;
}

/**
 * Get the initial login mode based on user preference
 * @returns true if should use password mode, false for email code mode
 */
export function getInitialLoginMode(): boolean {
  const preference = getLoginPreference();
  return preference === 'password';
}
